<script>
  import { Link, router, page } from '@inertiajs/svelte';

  let { children, user: userProp = null } = $props();

  // Pages may pass `user` explicitly; otherwise fall back to the
  // globally shared `current_user` Inertia prop.
  const user = $derived(userProp || $page?.props?.current_user || {});

  let showMobileMenu = $state(false);

  const initials = $derived(
    (user.email || '?')
      .split('@')[0]
      .split(/[._-]/)
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('') || '?'
  );

  const currentPath = $derived($page?.url || '');

  const navItems = [
    { href: '/dashboard', label: 'Dashboard' },
    { href: '/account/settings', label: 'Account Settings' },
  ];

  function isActive(href) {
    return currentPath.startsWith(href);
  }

  function toggleMobileMenu() {
    showMobileMenu = !showMobileMenu;
  }

  function logOut() {
    if (user.current_session_id) {
      router.delete(`/sessions/${user.current_session_id}`);
    } else {
      window.location.href = '/sign_in';
    }
  }
</script>

<div class="min-h-screen flex flex-col bg-page">
  <header class="bg-white border-b border-gray-200 sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-14">
        <div class="flex items-center gap-7">
          <Link href="/" class="flex items-center gap-2 text-lg font-extrabold tracking-wide text-brand-700">
            <svg class="h-5.5 w-5.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5M12 13v8"/>
            </svg>
            MASET
          </Link>
          <nav class="hidden sm:flex gap-1">
            {#each navItems as item}
              <Link
                href={item.href}
                class={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            {/each}
          </nav>
        </div>

        <div class="hidden sm:flex items-center gap-3">
          <button
            type="button"
            class="text-sm font-medium text-gray-500 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-100 transition-colors"
            onclick={logOut}
          >
            Log out
          </button>
          <div
            class="h-8 w-8 rounded-full bg-brand-100 text-brand-800 grid place-items-center text-xs font-bold"
            title={user.email}
          >
            {initials}
          </div>
        </div>

        <div class="flex items-center sm:hidden">
          <button
            type="button"
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600"
            onclick={toggleMobileMenu}
            aria-expanded={showMobileMenu}
          >
            <span class="sr-only">Open main menu</span>
            <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              {#if showMobileMenu}
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              {:else}
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              {/if}
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    {#if showMobileMenu}
      <div class="sm:hidden border-t border-gray-100">
        <div class="pt-2 pb-3 space-y-1 px-2">
          {#each navItems as item}
            <Link
              href={item.href}
              class={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive(item.href)
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              {item.label}
            </Link>
          {/each}
        </div>
        <div class="pt-3 pb-3 border-t border-gray-100">
          <div class="flex items-center gap-3 px-4">
            <div class="h-8 w-8 rounded-full bg-brand-100 text-brand-800 grid place-items-center text-xs font-bold">
              {initials}
            </div>
            <div class="flex-grow text-sm font-medium text-gray-500 truncate">{user.email}</div>
            <button
              type="button"
              class="text-sm font-medium text-gray-500 hover:text-gray-900 px-2 py-1.5 rounded-md hover:bg-gray-100"
              onclick={logOut}
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    {/if}
  </header>

  <main class="flex-1">
    <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      {@render children()}
    </div>
  </main>

  <footer class="mt-auto">
    <div class="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
      <p class="text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Maset — Home Inventory Management
      </p>
    </div>
  </footer>
</div>
